import Checkbox from '@ben/orion/checkbox';
import Select from '@ben/orion/deprecated-select';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  IModularBenefitDetail,
  RecalculationConfig,
} from '@ben/shared/types/columbia';
import { memo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import {
  TIME_UNIT_OPTIONS,
  fieldCannotBeBlankErrorMessage,
} from '../../shared/constants';
import PreRecalculationRule from './pre-recalculation-rule';

const SOURCE_FIELD_OPTIONS = [
  { label: 'Employment Start Date', key: 'EMPLOYMENT_START' },
  { label: 'Probation End Date', key: 'PROBATION_END' },
  { label: 'Benefit Start Date', key: 'BENEFIT_START' },
  { label: 'Dependant Birthdays', key: 'DEPENDANT_BIRTHDAYS' },
  { label: 'Employee Birthday', key: 'EMPLOYEE_BIRTHDAY' },
];

const DynamicRecalculationConfig = ({
  errors: _errors,
}: {
  errors: unknown;
}) => {
  const { control, setValue } = useFormContext<IModularBenefitDetail>();

  const { fields, append, remove } = useFieldArray({
    name: 'recalculationConfigs',
    control,
  });

  const addNewRecalculationConfig = () => {
    if (fields.length === 0) setValue('recalculationConfigs', []);
    const newRecalculationConfig = {
      id: undefined,
      sourceField: 'EMPLOYMENT_START',
      frequencyValue: 1,
      frequencyUnit: 'YEARS',
      isActive: true,
      preRecalculationMinFormula: null,
      targetUserInputKey: null,
    } as RecalculationConfig;

    append(newRecalculationConfig);
  };

  return (
    <>
      <AddWrapper>
        <StyledInlineTextButton
          label="Add dynamic recalculation config"
          aria-label="Add dynamic recalculation config"
          onClick={addNewRecalculationConfig}
        />
      </AddWrapper>

      {fields?.length === 0 && (
        <HeadingWrapper>
          <Typography variant={TypographyVariant.bodyLg1}>
            No dynamic recalculation config added yet
          </Typography>
        </HeadingWrapper>
      )}

      {fields.map((recalculationConfig: RecalculationConfig, index: number) => {
        return (
          <div key={recalculationConfig.id ?? `new-${index}`}>
            <HeadingWrapper>
              <Typography variant={TypographyVariant.bodyLg2}>
                Dynamic recalculation config #{index + 1}
              </Typography>
              <StyledInlineTextButton
                label="Remove"
                aria-label="Remove"
                onClick={() => remove(index)}
              />
            </HeadingWrapper>
            <RowWrapper>
              <Controller
                name={`recalculationConfigs.${index}.isActive`}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    value={field.value ?? false}
                    label="Active"
                  />
                )}
              />
              <Controller
                name={`recalculationConfigs.${index}.sourceField`}
                control={control}
                rules={{
                  required: fieldCannotBeBlankErrorMessage,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      name="Source field unit"
                      options={SOURCE_FIELD_OPTIONS}
                      hasError={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      hintText="The field to recalculate the price from, such as 'employment_start' or 'date_of_birth' of the employee"
                    />
                  );
                }}
              />
              <Controller
                name={`recalculationConfigs.${index}.frequencyValue`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    name="Frequency value"
                    width="120px"
                    hintText="The value of the frequency of recalculation"
                  />
                )}
              />
              <Controller
                name={`recalculationConfigs.${index}.frequencyUnit`}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    name="Frequency unit"
                    options={TIME_UNIT_OPTIONS}
                    width="150px"
                    hintText="The unit of time for the frequency value"
                  />
                )}
              />
            </RowWrapper>
            <PreRecalculationRule
              index={index}
              recalculationConfig={recalculationConfig}
            />
          </div>
        );
      })}
    </>
  );
};

export default memo(DynamicRecalculationConfig);

const AddWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => theme.typography.bodyLg2};
`;

const RowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(4)};
    margin-top: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(2)};
  `}
`;
